import { inject, Injectable } from '@angular/core';
import { FlightApi } from './flight.api';
import {
  catchError,
  EMPTY,
  expand,
  map,
  Observable,
  share,
  Subject,
  switchMap,
  takeWhile,
  timer,
} from 'rxjs';
import {
  FlightResearchResponse,
  FlightSearchResponse,
} from '../../models/flight-search-response';
import { UpsellingRequest } from '../../models/upselling-request';
import { PayRequest } from '../../models/pay-request';
import { TripType } from '../../types/trip-type';
import { Airport } from '../../models/search-airport';
import { FilterObject } from '../../models/filter-object';
import { HttpParams } from '@angular/common/http';
import { NotificationService } from '../../../../core/services/notification.service';
import { CurrencyService } from '../../../../core/services/currency.service';

@Injectable({
  providedIn: 'root',
})
export class FlightService {
  private flightApi = inject(FlightApi);
  private notificationService = inject(NotificationService);
  private currencyService = inject(CurrencyService);

  ///////////////////////////////////////////////////////////////////////////////////////////

  // emits when a research poll fails, so the results page can show "The search stopped"
  researchError$ = new Subject<void>();

  private researchTrigger$ = new Subject<{
    searchId: string;
    tripType?: TripType;
    filters?: FilterObject;
  }>();

  researchFlights$: Observable<FlightResearchResponse> =
    this.researchTrigger$.pipe(
      switchMap(({ searchId, tripType = 'One-way', filters }) => {
        const params = this.buildHttpParams(filters);
        let attempt = 0;

        return this.flightApi.researchFlights(searchId, params).pipe(
          expand(() => {
            attempt++;
            return timer(3000).pipe(
              switchMap(() => this.flightApi.researchFlights(searchId, params))
            );
          }),
          takeWhile(
            (res) =>
              res?.response?.responsePercentage !== '100.0%' && attempt < 10,
            true
          ),
          map((response) => {
            if (attempt >= 10) {
              response = {
                ...response,
                response: {
                  ...response.response,
                  responsePercentage: '100.0%',
                },
              };
            }
            return response;
          }),
          catchError(() => {
            this.researchError$.next();
            return EMPTY;
          })
        );
      }),
      share()
    );

  triggerResearch(
    searchId: string,
    tripType?: TripType,
    filters?: FilterObject
  ) {
    this.researchTrigger$.next({ searchId, tripType, filters });
  }

  //////////////////////////////////////////////////////////////////////////////////////////

  searchAirports(keyword: string): Observable<Airport[]> {
    return this.flightApi.searchAirports(keyword).pipe(
      map((airportRes: Airport[]) =>
        airportRes.map((airport) => ({
          ...airport,
          labelname: `${airport.city} (${airport.iata}), ${airport.country}`,
        }))
      )
    );
  }

  searchFlights(
    body: any,
    filters?: FilterObject
  ): Observable<FlightResearchResponse> {
    let newBody = this.prepareSearchBody(body, body?.tripType);

    const isMultiCity = body?.tripType === 'Multi City';

    let observable = isMultiCity
      ? this.flightApi.searchFlightsMultiCity(newBody)
      : this.flightApi.searchFlights(newBody);

    return observable.pipe(
      catchError((error) => {
        this.notificationService.showError(error);
        return EMPTY;
      }),
      switchMap((flightResponse) =>
        this.triggerSearchAndReturn(
          flightResponse.searchId,
          body?.tripType,
          filters
        )
      )
    );
  }

  private triggerSearchAndReturn(
    searchId: string,
    tripType?: TripType,
    filters?: FilterObject
  ) {
    this.triggerResearch(searchId, tripType, filters);
    return this.researchFlights$;
  }

  //////////////////////////////////////////////////////////////////////////////////////////

  private buildHttpParams(filters?: FilterObject): HttpParams {
    let params = new HttpParams();

    if (!filters) return params;

    const filterMap: Record<string, any> = {
      pageNumber: filters.pageNumber,
      pageSize: filters.pageSize,
      selectedMinPrice: filters.selectedMinPrice,
      selectedMaxPrice: filters.selectedMaxPrice,
      selectedAirlines: filters.selectedAirlines,
      selectedStops: filters.selectedStops,
      selectedMinStopDuration: filters.selectedMinStopDuration,
      selectedMaxStopDuration: filters.selectedMaxStopDuration,
      sortType: filters.sortType,
    };

    let finalParams = this.getFinalParams(filterMap);

    Object.entries(finalParams).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        if (Array.isArray(value)) {
          value.forEach((v: any) => {
            params = params.append(key, String(v));
          });
        } else params = params.set(key, value);
      }
    });
    return params;
  }

  getFinalParams(filterMap: Record<string, any>) {
    return Object.entries(filterMap).reduce((acc, [key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        acc[key] = value;
      }
      return acc;
    }, {} as { [key: string]: any });
  }

  prepareSearchBody(payload: any, tripType: TripType): any {
    const { formValue, passengers } = payload;
    const trips = formValue.trips || [];

    if (tripType === 'Multi City') {
      return this.payloadMultiCity(payload);
    }

    const firstTrip = trips[0];

    const basePayload = {
      maxNumberOfConnections: formValue.maxNumberOfConnections,
      travelClass: formValue.classType,
      adult: passengers.adult,
      child: passengers.child,
      infant: passengers.infant,
      currency: this.currencyService.currentCurrency(),
      departureAirport: firstTrip.departureAirport.iata,
      arrivalAirport: firstTrip.arrivalAirport.iata,
      departureDate: firstTrip.departDate,
    };

    if (tripType === 'Round Trip') {
      return {
        ...basePayload,
        returnDate: firstTrip?.returnDate,
      };
    }

    return basePayload;
  }

  payloadMultiCity(payload: any) {
    const { formValue, passengers } = payload;
    const trips = formValue.trips || [];

    const flightSegments = trips.map((trip: any) => ({
      departureAirport: trip.departureAirport?.iata,
      arrivalAirport: trip.arrivalAirport?.iata,
      departureDate: trip.departDate,
    }));

    return {
      maxNumberOfConnections: formValue.maxNumberOfConnections,
      adult: passengers.adult,
      child: passengers.child,
      infant: passengers.infant,
      travelClass: formValue.classType,
      currency: this.currencyService.currentCurrency(),
      flightSegments,
    };
  }
  //////////////////////////////////////////////////////////////////////////////////////////

  getUpselling(
    upsellingRequest: UpsellingRequest
  ): Observable<FlightSearchResponse> {
    return this.flightApi.getFlightsupselling(upsellingRequest).pipe(
      catchError((error) => {
        this.notificationService.showError(error);
        throw error;
      })
    );
  }

  confirmPrice(
    upsellingRequest: UpsellingRequest,
    bags: boolean = true,
    meals: boolean = true
  ): Observable<any> {
    return this.flightApi.confirmPrice(upsellingRequest, bags, meals).pipe(
      catchError((error) => {
        if (error?.status !== 0) this.notificationService.showError(error);
        throw error;
      })
    );
  }
  //////////////////////////////////////////////////////////////////////////////////////////

  pay(paymentRequest: PayRequest): Observable<any> {
    return this.flightApi.pay(paymentRequest);
  }
}
